import { BreakpointObserver } from '@angular/cdk/layout';
import { DatePipe } from '@angular/common';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { BaseDto } from '@models/base/baseDto.model';
import { Genericos } from '@models/base/genericos.model';
import { RangoFechasValores } from '@models/base/identificadores.model';
import { PagoComprobanteDetailDto } from '@models/pagos-comprobantes/pagoComprobanteDetailDto.model';
import { PagoComprobanteFilter } from '@models/pagos-comprobantes/pagoComprobanteFilter.model';
import { HotToastService } from '@ngneat/hot-toast';
import { EstadosPagoService } from '@services/estados-pago/estados-pago.service';
import { FormasPagoService } from '@services/formas-pago/formas-pago.service';
import { PagoscomprobantesService } from '@services/pagos-comprobantes/pagoscomprobantes.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { isFalsy } from 'utility-types';

interface TableColumn {
  key: string;
  label: string;
  visible: boolean;
  sortable?: boolean;
}

@Component({
  selector: 'app-listado-acreditaciones',
  templateUrl: './listado-acreditaciones.component.html',
  styleUrls: ['./listado-acreditaciones.component.css'],
  providers: [DatePipe],
})
export class ListadoAcreditacionesComponent implements OnInit {

  form!: FormGroup;
  rangoFecha: number = RangoFechasValores.Hoy;
  private paginator!: MatPaginator;
  private sort!: MatSort;
  allRowsExpanded: boolean = false;
  checkedFiltroAvanzado: boolean = false;
  formBuilder = new FormBuilder();
  dataSource = new MatTableDataSource<PagoComprobanteDetailDto>();
  lstFormasPago!: BaseDto[];
  lstEstadosPago!: BaseDto[];
  lstRangoFecha = Genericos.lstRangoFecha;
  @Input() idContrato!: number;
  data = false;
  displayedColumns: string[] = [];

  // Configuración de columnas
  availableColumns: TableColumn[] = [
    { key: 'estado', label: 'Estado', visible: true, sortable: true },
    { key: 'fechaPago', label: 'Fecha Pago', visible: true, sortable: true },
    { key: 'fechaAcreditacion', label: 'Fecha Acreditación', visible: true, sortable: true },
    { key: 'numeroComprobante', label: 'Nro. Comprobante', visible: true, sortable: true },
    { key: 'contrato', label: 'Contrato', visible: true, sortable: true },
    { key: 'estadoPago', label: 'Estado Pago', visible: true, sortable: true },
    { key: 'formaPago', label: 'Forma Pago', visible: true, sortable: true },
    { key: 'importe', label: 'Importe', visible: true, sortable: true },
    { key: 'descripcion', label: 'Descripción', visible: true, sortable: true }
  ];

  // Columnas para móvil
  mobileColumns: string[] = ['fecha', 'importe', 'estadoPago'];

  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.sort = ms;
    this.setDataSourceAttributes();
  }

  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.setDataSourceAttributes();
  }

  constructor(
    private pagoscomprobantesService: PagoscomprobantesService,
    private toastService: HotToastService,
    private snackBarService: SnackBarService,
    private snackbar: MatSnackBar,
    private spinner: NgxSpinnerService,
    public dialog: MatDialog,
    private datepipe: DatePipe,
    private formasPagoService: FormasPagoService,
    private breakpointObserver: BreakpointObserver,
    private estadosPagoService: EstadosPagoService
  ) {
    this.initializeForm();
    this.setupResponsiveColumns();
    this.setupCustomSort();
  }

  private initializeForm(): void {
    this.form = this.formBuilder.group({
      FechaDesde: [new Date((new Date().getTime() - RangoFechasValores.Hoy)), Validators.required],
      FechaHasta: [new Date(), Validators.required],
      FechaAcreditacionDesde: [null],
      FechaAcreditacionHasta: [null],
      RangoFecha: [''],
      FormasPago: [''],
      EstadosPago: ['']
    });
  }

  private setupResponsiveColumns(): void {
    this.breakpointObserver.observe(['(max-width: 600px)']).subscribe(result => {
      if (result.matches) {
        // Vista móvil - solo columnas esenciales
        this.displayedColumns = this.mobileColumns.filter(col =>
          this.availableColumns.find(c => c.key === col)?.visible
        );
      } else {
        // Vista desktop - todas las columnas visibles
        this.updateDisplayedColumns();
      }
    });
  }

  private setupCustomSort(): void {
    // Configurar accessor personalizado para ordenamiento de objetos anidados
    this.dataSource.sortingDataAccessor = (data: PagoComprobanteDetailDto, sortHeaderId: string) => {
      switch (sortHeaderId) {
        case 'fechaPago':
          return data.pago?.fechaPago ? new Date(data.pago.fechaPago).getTime() : 0;
        case 'fechaAcreditacion':
          return data.pago?.fechaAcreditacion ? new Date(data.pago.fechaAcreditacion).getTime() : 0;
        case 'numeroComprobante':
          return data.comprobante?.nroComprobante || '';
        case 'contrato':
          return data.comprobante?.contrato?.descripcion || '';
        case 'estadoPago':
          return data.pago?.estadoPago?.descripcion || '';
        case 'formaPago':
          return data.pago?.formaPago?.descripcion || '';
        case 'importe':
          return data.pago?.importe || 0;
        case 'descripcion':
          return data.pago?.descripcion || '';
        default:
          return '';
      }
    };
  }

  ngOnInit(): void {
    this.getAllFormasPago();
    this.getAllEstadosPago();
    this.updateDisplayedColumns();

    if (this.idContrato) {
      this.filtrar();
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  private setDataSourceAttributes(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    if (this.paginator && this.sort) {
      this.applyFilter(null);
    }
  }

  // Gestión de columnas visibles
  updateDisplayedColumns(): void {
    this.displayedColumns = this.availableColumns
      .filter(col => col.visible)
      .map(col => col.key);
  }

  isColumnVisible(columnKey: string): boolean {
    return this.availableColumns.find(col => col.key === columnKey)?.visible || false;
  }

  toggleColumn(columnKey: string, visible: boolean): void {
    const column = this.availableColumns.find(col => col.key === columnKey);
    if (column) {
      column.visible = visible;
      this.updateDisplayedColumns();
    }
  }

  // Filtros y búsqueda
  applyFilter(event: any): void {
    if (event && event.target && event.target.value !== undefined) {
      const filterValue = (event.target as HTMLInputElement).value;
      this.dataSource.filter = filterValue.trim().toLowerCase();

      if (this.dataSource.paginator) {
        this.dataSource.paginator.firstPage();
      }
    }
  }

  selectRangoFecha(event: any): void {
    this.rangoFecha = event.value;
    if (this.rangoFecha == RangoFechasValores.Todos) {
      this.form.controls["FechaDesde"].setValue(new Date((new Date().getTime() - this.rangoFecha)));
      this.form.controls["FechaHasta"].setValue(new Date((new Date().getTime() + this.rangoFecha)));
    } else {
      this.form.controls["FechaDesde"].setValue(new Date((new Date().getTime() - this.rangoFecha)));
      this.form.controls["FechaHasta"].setValue(new Date((new Date().getTime())));
    }
  }

  filtrar(): void {
    if (!this.form.valid) {
      this.snackBarService.showError('Por favor, complete los campos requeridos', 'Error');
      return;
    }

    const fechaDesde = this.datepipe.transform(this.form.controls["FechaDesde"].value, 'yyyy-MM-dd') + ' 00:00:00';
    const fechaHasta = this.datepipe.transform(this.form.controls["FechaHasta"].value, 'yyyy-MM-dd') + ' 23:59:59';

    const fechaAcredDesde = this.form.controls["FechaAcreditacionDesde"].value
      ? this.datepipe.transform(this.form.controls["FechaAcreditacionDesde"].value, 'yyyy-MM-dd') + ' 00:00:00'
      : null;

    const fechaAcredHasta = this.form.controls["FechaAcreditacionHasta"].value
      ? this.datepipe.transform(this.form.controls["FechaAcreditacionHasta"].value, 'yyyy-MM-dd') + ' 23:59:59'
      : null;

    const filter: PagoComprobanteFilter = {
      dateFrom: fechaDesde!,
      dateTo: fechaHasta!,
      fechaAcreditacionDesde: fechaAcredDesde,
      fechaAcreditacionHasta: fechaAcredHasta,
      idsFormaPago: this.form.controls["FormasPago"].value || null,
      idsEstadoPago: this.form.controls["EstadosPago"].value || null,
      idContrato: isFalsy(this.idContrato) ? null : this.idContrato
    };

    this.spinner.show();

    this.pagoscomprobantesService.getPagosComprobantesByFilter(filter).subscribe({
      next: (data) => {
        this.spinner.hide();
        this.dataSource.data = data;
        this.data = this.dataSource.data.length >= 1;
        if (data.length === 0)
          this.snackBarService.showInfo('No se encontraron registros con los filtros aplicados', 'Información');
      },
      error: (error) => {
        this.spinner.hide();
        this.snackBarService.showError(error, "Error");
      }
    });
  }

  // Servicios de datos
  getAllEstadosPago(): void {
    this.spinner.show("spBusquedaEstadosPagos");

    this.estadosPagoService.getAll().subscribe({
      next: (data) => {
        this.spinner.hide("spBusquedaEstadosPagos");
        this.lstEstadosPago = data;
      },
      error: (error) => {
        this.spinner.hide("spBusquedaEstadosPagos");
        this.snackBarService.showError(error, "Error");
      }
    });
  }

  getAllFormasPago(): void {
    this.spinner.show("spBusquedaFormasPago");

    this.formasPagoService.getAll().subscribe({
      next: (data) => {
        this.spinner.hide("spBusquedaFormasPago");
        this.lstFormasPago = data;
      },
      error: (error) => {
        this.spinner.hide("spBusquedaFormasPago");
        this.snackBarService.showError(error, "Error");
      }
    });
  }

  // Métodos auxiliares
  isFalsy(valor: any): boolean {
    return isFalsy(valor);
  }

  trackByItems(index: number, item: any): any {
    return item;
  }

  // Método para exportar datos (opcional)
  exportData(): void {
    // Implementar exportación si es necesario
    console.log('Exportar datos:', this.dataSource.filteredData);
  }

  limpiarFiltros(): void {
    this.form.patchValue({
      FechaDesde: new Date((new Date().getTime() - RangoFechasValores.Hoy)),
      FechaHasta: new Date(),
      FechaAcreditacionDesde: null,
      FechaAcreditacionHasta: null,
      RangoFecha: '',
      FormasPago: '',
      EstadosPago: ''
    });
    this.dataSource.data = [];
    this.data = false;
  }

}
